import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import imgSizes from "conf/imgSizes.json";
import useAuth from "common/hooks/useAuth";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import usePark from "common/hooks/usePark";
import SpecySelect from "components/select/SpecySelect/SpecySelect";
import { Animal } from "types/Animal";
import ObservationGroupSelect from "components/select/ObservationGroupSelect/ObservationGroupSelect";
import TagSelect from "components/select/TagSelect/TagSelect";
import { ObservationGroup } from "types/ObservationGroup";
import { Tag } from "types/Tag";
import EnclosureSelect from "components/select/EnclosureSelect/EnclosureSelect";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import RadioBlockItem from "common/components/RadioBlockItem/RadioBlockItem";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import AnimalGenderSelect from "components/select/AnimalSelect/AnimalGenderSelect";
import AnimalReproductiveStatusSelect from "components/select/AnimalSelect/AnimalReproductiveStatusSelect";
import { Enclosure } from "types/Enclosure";
import Drawer from "common/components/Drawer/Drawer";
import SpecyForm from "pages/Species/SpecyForm";
import useItem from "common/hooks/useItem";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

type Props = {
  animal: Animal & { lastObservationDate?: any };
  onSubmit: (value?: any) => any;
  isFromImport?: boolean;
};

function AnimalForm({ onSubmit, animal, isFromImport }: Props) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { park: currentPark } = usePark();
  const canManageAnimal = useMemo(() => {
    const {
      isAdmin,
      isSuperadmin,
      isManager,
      isObserver,
      administrationPermissions,
    } = currentUser || {};
    const permissions = { ...administrationPermissions?.animal };

    return animal?.id
      ? isAdmin ||
          isSuperadmin ||
          ((isManager || isObserver) && permissions?.update)
      : isAdmin ||
          isSuperadmin ||
          ((isManager || isObserver) && permissions?.create);
  }, [animal, currentUser]);

  const [isBirthDateKnown, setIsBirthDateKnown] = useState(false);

  const initialValues: any = useMemo(
    () =>
      animal
        ? {
            ...animal,
            park: animal.park?.id ?? currentPark?.id,
            species: animal?.species?.id ?? animal?.species,
            enclosures: animal?.enclosures?.map((e: Enclosure) => e?.id),
            observationGroups: animal?.observationGroups?.map(
              (og: ObservationGroup) => og.id
            ),
            tags: animal?.tags?.map((tag: Tag) => tag.id),
          }
        : {
            houseName: "",
            isGroup: false,
            park: currentPark?.id,
          },
    [animal, currentPark]
  );

  const onSubmitWrapper = async (values: any) => {
    const newValues = { ...values };
    if (typeof values.observationGrids === "number") {
      newValues.observationGrids = [{ id: values.observationGrids }];
    }

    const res = await onSubmit(newValues);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  useEffect(() => {
    if (initialValues.isEstimatedBirthDate) {
      setIsBirthDateKnown(false);
    } else {
      setIsBirthDateKnown(!!initialValues.birthDate);
    }
  }, [initialValues]);

  // Code spécifique l'ajout d'une espèce depuis le sélecteur
  const [openDrawer, setOpenDrawer] = useState(false);
  const [refreshItem, setRefreshKey] = useState(false);

  const { saveItem }: any = useItem("species", null, {});

  const handleSubmitSpeciesForm = useCallback(
    async (form: any, newSpeciesValue: any) => {
      try {
        const newItem = await saveItem(newSpeciesValue);
        form.mutators.setFieldValue({
          field: "species",
          value: newItem.id,
        });
        setRefreshKey(newItem);
      } finally {
        setOpenDrawer(false);
      }
    },
    [saveItem]
  );

  const handleCancel = useCallback(() => {
    if (isFromImport) {
      navigate(-1);
    } else {
      navigate("/animals", { replace: true });
    }
  }, [isFromImport, navigate]);

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      render={({ form, handleSubmit, values }) => {
        const calculateAge = (birthDate: string | number | Date) => {
          const today = new Date();
          const date = new Date(birthDate);

          const diffTime = Math.abs(today.getTime() - date.getTime());
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

          const years = Math.floor(diffDays / 365);
          const months = Math.floor((diffDays % 365) / 30);
          const days = (diffDays % 365) % 30;

          return { years, months, days };
        };

        const estimatedBirthDate = values.estimatedBirthDate
          ? calculateAge(values.estimatedBirthDate)
          : { years: 0, months: 0, days: 0 };
        const birthDate = values.birthDate
          ? calculateAge(values.birthDate)
          : { years: 0, months: 0, days: 0 };

        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(animal?.id ? "editItem.animal" : "newItem.animal")}
              subtitle={values.name}
              icon="IoPawOutline"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={canManageAnimal ? handleSubmit : undefined}
                  onCancel={handleCancel}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />
              <div className="responsive-flex-lg gap-2 mt-2">
                <div className="md:w-2/3 flex flex-col gap-2">
                  <div className="white-box">
                    <SectionTitle
                      title={t("forms.identity")}
                      className="mb-4 text-lg normal-case"
                    />
                    <div className="responsive-flex gap-4">
                      <div className="flex flex-col w-full gap-4">
                        <FormControl
                          type="custom"
                          title={t("common.type")}
                          name="isGroup"
                          inputContainerStyle="flex gap-3 mb-4 w-full"
                          containerStyle="w-full"
                          editable={canManageAnimal}
                          required
                        >
                          <RadioBlock<boolean>
                            Component={RadioBlockItem}
                            items={[
                              {
                                id: false,
                                icon: "IoPawOutline",
                                titleKey: "common.animal",
                                color: "#00492C",
                              },
                              {
                                id: true,
                                icon: "BsStarHalf",
                                titleKey: "common.individualsGroup",
                                color: "#FBBA19",
                              },
                            ]}
                            horizontal
                          />
                        </FormControl>
                        <div className="flex flex-row flex-1 gap-4">
                          <FormControl
                            name="houseName"
                            title={t("common.name")}
                            editable={canManageAnimal}
                            containerStyle="flex-1"
                          />
                          {values.isGroup && (
                            <FormControl
                              name="individualsNumber"
                              title={t("forms.individualsNumber")}
                              type="number"
                              editable={canManageAnimal}
                              required={values.isGroup}
                            />
                          )}
                        </div>
                      </div>
                      <FormControl
                        name="picture"
                        title={t("forms.image")}
                        type="image"
                        maxWidth={imgSizes.logo.width}
                        maxHeight={imgSizes.logo.height}
                        editable={canManageAnimal}
                        disabled={!canManageAnimal}
                        labelIcon="PhotoIcon"
                        className="border border-slate-200 rounded-lg overflow-hidden"
                      />
                    </div>
                    <div className="flex flex-row w-full gap-4 mt-4">
                      <div className="flex-1 flex flex-col gap-4">
                        <FormControl
                          name="species"
                          title={t("animals.species")}
                          editable={canManageAnimal}
                          type="custom"
                        >
                          <SpecySelect
                            enableAddNewEntry
                            onAddNewData={() => setOpenDrawer(true)}
                            refreshItem={refreshItem}
                          />
                        </FormControl>
                        {values.isGroup ? (
                          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            <FormControl
                              name="nbMaleInGroup"
                              title={t("animals.nbMale")}
                              editable={canManageAnimal}
                              type="number"
                            />
                            <FormControl
                              name="nbFemaleInGroup"
                              title={t("animals.nbFemale")}
                              editable={canManageAnimal}
                              type="number"
                            />
                            <FormControl
                              name="nbUndefinedGenderInGroup"
                              title={t("animals.nbUndefinedGender")}
                              editable={canManageAnimal}
                              type="number"
                            />
                          </div>
                        ) : (
                          <FormControl
                            name="gender"
                            title={t("animals.gender")}
                            editable={canManageAnimal}
                            type="custom"
                          >
                            <AnimalGenderSelect />
                          </FormControl>
                        )}
                        <FormControl
                          name="reproductiveStatus"
                          title={t("animals.reproductiveStatus")}
                          editable={canManageAnimal}
                          type="custom"
                        >
                          <AnimalReproductiveStatusSelect />
                        </FormControl>
                      </div>
                      {!values.isGroup && (
                        <div className="flex-1 flex flex-col gap-4 mt-6">
                          <ToggleSwitch
                            rightLabel={t("forms.knownBirthDate")}
                            value={isBirthDateKnown}
                            isDisabled={!canManageAnimal}
                            onChange={(value: boolean) => {
                              if (value) {
                                form.mutators.setFieldValue({
                                  field: "estimatedBirthDate",
                                  value: null,
                                });
                              } else {
                                form.mutators.setFieldValue({
                                  field: "birthDate",
                                  value: null,
                                });
                              }
                              setIsBirthDateKnown(value);
                            }}
                          />
                          {isBirthDateKnown ? (
                            <div className="flex flex-col gap-2">
                              <FormControl
                                name="birthDate"
                                title={t("forms.birthDate")}
                                editable={canManageAnimal}
                                type="date"
                                showTimeSelect={false}
                                showYearDropdown
                                locale={currentUser?.language ?? "fr"}
                              />
                              {values?.birthDate && (
                                <span>
                                  {t("animals.ageIs", {
                                    years: birthDate?.years,
                                    months: birthDate?.months,
                                    days: birthDate?.days,
                                  })}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-2">
                              <FormControl
                                name="estimatedBirthDate"
                                title={t("forms.estimatedBirthDate")}
                                editable={canManageAnimal}
                                type="date"
                                showTimeSelect={false}
                                showYearDropdown
                                locale={currentUser?.language ?? "fr"}
                              />
                              {values.estimatedBirthDate && (
                                <span>
                                  {t("animals.estimatedAgeIs", {
                                    years: estimatedBirthDate?.years,
                                    months: estimatedBirthDate?.months,
                                    days: estimatedBirthDate?.days,
                                  })}
                                </span>
                              )}
                            </div>
                          )}
                          <FormControl
                            name="isGeriatric"
                            title={t("animals.isGeriatric")}
                            editable={canManageAnimal}
                            type="checkbox"
                            className="rounded-sm ring-0 focus:ring-0 text-secondary"
                          />
                        </div>
                      )}
                    </div>
                    <p className="mt-4">
                      {initialValues.lastObservationDate
                        ? t("animals.lastObservationDate", {
                            date: dayjs(
                              initialValues.lastObservationDate
                            ).fromNow(),
                          })
                        : t("animals.notObservedYet")}
                    </p>
                  </div>
                  <div className="white-box">
                    <div className="w-full">
                      <SectionTitle
                        title={t("forms.identifiers")}
                        className="mb-4 text-lg normal-case"
                      />

                      <div className="flex flex-row w-full gap-4">
                        <div className="flex flex-col gap-4 flex-1">
                          <FormControl
                            name="localId"
                            title={
                              currentPark?.hasZimsImport
                                ? t("animals.localId")
                                : t("animals.local")
                            }
                            editable={canManageAnimal}
                          />
                          <FormControl
                            name="zimsId"
                            title={
                              currentPark?.hasZimsImport
                                ? t("animals.zimsId")
                                : t("animals.otherId")
                            }
                            editable={canManageAnimal}
                          />
                          <FormControl
                            name="band"
                            title={t("animals.band")}
                            editable={canManageAnimal}
                          />
                        </div>
                        <div className="flex flex-col gap-4 flex-1">
                          <FormControl
                            name="transponderId"
                            title={t("animals.transponderId")}
                            editable={canManageAnimal}
                          />
                          <FormControl
                            name="tag"
                            title={t("animals.tag")}
                            editable={canManageAnimal}
                            type="textarea"
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full md:w-1/3">
                  <div className="white-box">
                    <SectionTitle
                      title={t("forms.park")}
                      className="mb-4 text-lg normal-case"
                    />
                    <div className="responsive-flex gap-4">
                      <div className="flex flex-col w-full gap-4">
                        <FormControl
                          name="enclosures"
                          title={t("forms.enclosures")}
                          type="custom"
                          editable={canManageAnimal}
                        >
                          <EnclosureSelect multiple />
                        </FormControl>
                        <FormControl
                          name="tags"
                          title={t("forms.tags")}
                          editable={canManageAnimal}
                          type="custom"
                        >
                          <TagSelect multiple />
                        </FormControl>
                        <FormControl
                          name="observationGroups"
                          title={t("forms.observationGroups")}
                          editable={canManageAnimal}
                          type="custom"
                        >
                          <ObservationGroupSelect multiple />
                        </FormControl>
                      </div>
                    </div>
                  </div>

                  <div className="white-box mt-2">
                    <div className="w-full">
                      <SectionTitle
                        title={t("forms.food")}
                        className="mb-4 text-lg normal-case"
                      />

                      <div className="flex flex-col w-full gap-4">
                        {animal?.id && animal?.species?.food ? (
                          <FormControl
                            name="species.food"
                            value={animal?.species?.food as any}
                            type="textarea"
                            editable={false}
                            disabled
                            className="w-full"
                            title={t("forms.speciesFood")}
                          />
                        ) : null}
                        <FormControl
                          name="food"
                          type="textarea"
                          editable={canManageAnimal}
                          className="w-full"
                          title={t("forms.animalFood")}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="white-box mt-2">
                    <div className="w-full">
                      <SectionTitle
                        title={t("forms.medical")}
                        className="mb-4 text-lg normal-case"
                      />

                      <div className="flex flex-col w-full gap-4">
                        <FormControl
                          name="medicalProblem"
                          type="textarea"
                          editable={canManageAnimal}
                          className="w-full"
                          title={t("forms.medicalProblem")}
                        />
                        <FormControl
                          name="otherMedicalProblem"
                          type="textarea"
                          editable={canManageAnimal}
                          className="w-full"
                          title={t("forms.otherMedicalProblem")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <HeaderButtonsEdit
                onSubmit={handleSubmit}
                onCancel={handleCancel}
              />
            </form>
            <Drawer
              isOpen={openDrawer}
              onClose={() => setOpenDrawer(false)}
              anchor="right"
            >
              <SpecyForm
                onSubmit={(value: any) => handleSubmitSpeciesForm(form, value)}
                onCancel={() => setOpenDrawer(false)}
                additionalHeaderStyle="ml-12"
              />
            </Drawer>
          </div>
        );
      }}
    />
  );
}

export default AnimalForm;

/*
Nom, Espèce ; Numéro de bague, numéro de transpondeur, Numéro Zim’s/GAN ; numéro Zim’s local, les marques physiques (bague, boucle etc…) ;  ses enclos, secteurs, autres étiquettes
 */
